import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  Timestamp,
  getDocs
} from 'firebase/firestore';
import { db } from './firebaseConfig';

// Collection name
const COLLECTION_NAME = 'catalogueLeads';

// Lead statuses
export const LEAD_STATUSES = {
  NEW: 'new',
  CONTACTED: 'contacted'
};

export const STATUS_LABELS = {
  new: 'New',
  contacted: 'Contacted'
};

export const STATUS_COLORS = {
  new: '#1890ff',
  contacted: '#52c41a'
};

/**
 * Subscribe to catalogue leads with optional date range filtering
 * @param {Function} callback - Function to call with leads data
 * @param {Object} options - Optional filters (startDate, endDate)
 * @returns {Function} Unsubscribe function
 */
export const subscribeToCatalogueLeads = (callback, options = {}) => {
  try {
    const { startDate, endDate } = options;
    let q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));

    // Apply date range filters if provided
    if (startDate && endDate) {
      const start = Timestamp.fromDate(startDate);
      const end = Timestamp.fromDate(endDate);
      q = query(
        collection(db, COLLECTION_NAME),
        where('createdAt', '>=', start),
        where('createdAt', '<=', end),
        orderBy('createdAt', 'desc')
      );
    } else if (startDate) {
      const start = Timestamp.fromDate(startDate);
      q = query(
        collection(db, COLLECTION_NAME),
        where('createdAt', '>=', start),
        orderBy('createdAt', 'desc')
      );
    } else if (endDate) {
      const end = Timestamp.fromDate(endDate);
      q = query(
        collection(db, COLLECTION_NAME),
        where('createdAt', '<=', end),
        orderBy('createdAt', 'desc')
      );
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const leads = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Convert Firestore Timestamps to Date objects for easier handling
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          contactedDate: data.contactedDate?.toDate ? data.contactedDate.toDate() : data.contactedDate,
          followUpDate: data.followUpDate?.toDate ? data.followUpDate.toDate() : data.followUpDate,
        };
      });
      callback(leads);
    }, (error) => {
      console.error('Error subscribing to catalogue leads:', error);
      callback([]);
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up catalogue leads subscription:', error);
    return () => {};
  }
};

/**
 * Add a new catalogue lead
 * @param {Object} leadData - Lead information
 * @returns {Promise<string>} Document ID
 */
export const addCatalogueLead = async (leadData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      name: leadData.name || '',
      contactNumber: leadData.contactNumber || '',
      email: leadData.email || '',
      companyName: leadData.companyName || '',
      dateOfEnquiry: leadData.dateOfEnquiry || '',
      status: leadData.status || LEAD_STATUSES.NEW,
      contactedDate: leadData.contactedDate || null,
      followUpDate: leadData.followUpDate || null,
      notes: leadData.notes || '',
      timestamp: leadData.timestamp || new Date().toLocaleString('en-SG'),
      source: leadData.source || 'website',
      emailSent: leadData.emailSent !== undefined ? leadData.emailSent : false,
      createdAt: leadData.createdAt || serverTimestamp(),
    });
    
    console.log('Catalogue lead added with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error adding catalogue lead:', error);
    throw error;
  }
};

/**
 * Update lead status and set contacted date if status is 'contacted'
 * @param {string} leadId - Lead document ID
 * @param {string} newStatus - New status value
 * @returns {Promise<boolean>} Success status
 */
export const updateLeadStatus = async (leadId, newStatus) => {
  try {
    const leadRef = doc(db, COLLECTION_NAME, leadId);
    const updateData = { status: newStatus };
    
    // If changing to contacted status, record the date
    if (newStatus === LEAD_STATUSES.CONTACTED) {
      updateData.contactedDate = serverTimestamp();
    }
    
    await updateDoc(leadRef, updateData);
    console.log('Lead status updated:', leadId, newStatus);
    return true;
  } catch (error) {
    console.error('Error updating lead status:', error);
    return false;
  }
};

/**
 * Update lead notes
 * @param {string} leadId - Lead document ID
 * @param {string} notes - Notes text
 * @returns {Promise<boolean>} Success status
 */
export const updateLeadNotes = async (leadId, notes) => {
  try {
    const leadRef = doc(db, COLLECTION_NAME, leadId);
    await updateDoc(leadRef, { notes });
    console.log('Lead notes updated:', leadId);
    return true;
  } catch (error) {
    console.error('Error updating lead notes:', error);
    return false;
  }
};

/**
 * Update follow-up date
 * @param {string} leadId - Lead document ID
 * @param {Date|null} followUpDate - Follow-up date or null to clear
 * @returns {Promise<boolean>} Success status
 */
export const updateFollowUpDate = async (leadId, followUpDate) => {
  try {
    const leadRef = doc(db, COLLECTION_NAME, leadId);
    await updateDoc(leadRef, { 
      followUpDate: followUpDate ? Timestamp.fromDate(followUpDate) : null 
    });
    console.log('Follow-up date updated:', leadId);
    return true;
  } catch (error) {
    console.error('Error updating follow-up date:', error);
    return false;
  }
};

/**
 * Delete a catalogue lead
 * @param {string} leadId - Lead document ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteLead = async (leadId) => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, leadId));
    console.log('Lead deleted:', leadId);
    return true;
  } catch (error) {
    console.error('Error deleting lead:', error);
    return false;
  }
};

/**
 * Get catalogue lead statistics
 * @param {Object} options - Optional filters (startDate, endDate)
 * @returns {Promise<Object>} Statistics object
 */
export const getLeadStats = async (options = {}) => {
  try {
    const { startDate, endDate } = options;
    let q = query(collection(db, COLLECTION_NAME));

    // Apply date range filters if provided
    if (startDate && endDate) {
      const start = Timestamp.fromDate(startDate);
      const end = Timestamp.fromDate(endDate);
      q = query(
        collection(db, COLLECTION_NAME),
        where('createdAt', '>=', start),
        where('createdAt', '<=', end)
      );
    } else if (startDate) {
      const start = Timestamp.fromDate(startDate);
      q = query(
        collection(db, COLLECTION_NAME),
        where('createdAt', '>=', start)
      );
    } else if (endDate) {
      const end = Timestamp.fromDate(endDate);
      q = query(
        collection(db, COLLECTION_NAME),
        where('createdAt', '<=', end)
      );
    }

    const snapshot = await getDocs(q);
    const leads = snapshot.docs.map(doc => doc.data());

    const stats = {
      total: leads.length,
      new: leads.filter(l => l.status === LEAD_STATUSES.NEW).length,
      contacted: leads.filter(l => l.status === LEAD_STATUSES.CONTACTED).length,
    };

    return stats;
  } catch (error) {
    console.error('Error getting lead stats:', error);
    return { total: 0, new: 0, contacted: 0 };
  }
};
